import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Parent, User } from '../types/models';
import { authApi } from '../api/apiService';

interface AuthContextType {
  parent: Parent | null;
  kids: User[];
  currentKid: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  userType: 'parent' | 'kid' | null;
  loginWithGoogle: (idToken: string) => Promise<void>;
  loginKid: (username: string, pinCode: string) => Promise<void>;
  selectKid: (kidId: string) => void;
  logout: () => void;
  refreshKids: () => Promise<void>;
  pendingInviteCode: string | null;
  clearPendingInviteCode: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [parent, setParent] = useState<Parent | null>(null);
  const [kids, setKids] = useState<User[]>([]);
  const [currentKid, setCurrentKid] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingInviteCode, setPendingInviteCode] = useState<string | null>(null);
  const [userType, setUserType] = useState<'parent' | 'kid' | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for stored invite code on mount
    const storedInviteCode = sessionStorage.getItem('pendingInviteCode');
    if (storedInviteCode) {
      setPendingInviteCode(storedInviteCode);
    }
    checkAuth();

    // Listen for 401 unauthorized events from API interceptor
    const handleUnauthorized = () => {
      setParent(null);
      setKids([]);
      setCurrentKid(null);
      setUserType(null);
      navigate('/login');
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);

    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLocalNetwork = () => {
    const hostname = window.location.hostname;
    return hostname.startsWith('192.168.');
  };

  const checkAuth = async () => {
    const parentToken = localStorage.getItem('parentToken');
    const kidToken = localStorage.getItem('kidToken');
    const storedKidId = localStorage.getItem('selectedKidId');

    // Check kid token first
    if (kidToken) {
      try {
        const response = await authApi.getKidMe(kidToken);
        if (response.data.type === 'kid') {
          setCurrentKid(response.data.data);
          setParent(null);
          setKids([]);
          setUserType('kid');
          setLoading(false);
          return;
        }
      } catch (err) {
        localStorage.removeItem('kidToken');
        localStorage.removeItem('selectedKidId');
      }
    }

    if (parentToken) {
      try {
        const response = await authApi.getMe(parentToken);
        if (response.data.type === 'parent') {
          setParent(response.data.data);
          setUserType('parent');
          const kidsData = response.data.kids || [];
          setKids(kidsData);

          // Restore selected kid if stored
          if (storedKidId) {
            const kid = kidsData.find((k: User) => k.id === storedKidId);
            if (kid) {
              setCurrentKid(kid);
            }
          }
        }
        setLoading(false);
        return;
      } catch (err) {
        localStorage.removeItem('parentToken');
        localStorage.removeItem('selectedKidId');
      }
    }

    // If on local network and no token, try dev login
    if (isLocalNetwork()) {
      try {
        const response = await authApi.devLogin();
        const { token, parent: parentData, kids: kidsData } = response.data;
        localStorage.setItem('parentToken', token);
        setParent(parentData);
        setKids(kidsData);
        setUserType('parent');
      } catch (err) {
        console.error('Dev login failed:', err);
      }
    }

    setLoading(false);
  };

  const loginWithGoogle = async (idToken: string) => {
    try {
      setLoading(true);
      const response = await authApi.googleLogin(idToken);
      const { token, parent: parentData, kids: kidsData } = response.data;

      localStorage.setItem('parentToken', token);
      setParent(parentData);
      setKids(kidsData);
      setUserType('parent');

      // Navigation is handled by LoginPage useEffect watching isAuthenticated
      // But handle pending invite code specially
      const storedInviteCode = sessionStorage.getItem('pendingInviteCode');
      if (storedInviteCode) {
        sessionStorage.removeItem('pendingInviteCode');
        setPendingInviteCode(null);
        navigate(`/join-family/${storedInviteCode}`);
      }
    } catch (err) {
      console.error('Login failed:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const selectKid = (kidId: string) => {
    const kid = kids.find(k => k.id === kidId);
    if (kid) {
      localStorage.setItem('selectedKidId', kidId);
      setCurrentKid(kid);
      navigate(`/user/${kidId}/targets`);
    }
  };

  const logout = () => {
    localStorage.removeItem('parentToken');
    localStorage.removeItem('kidToken');
    localStorage.removeItem('selectedKidId');
    setParent(null);
    setKids([]);
    setCurrentKid(null);
    setUserType(null);
    navigate('/login');
  };

  const loginKid = async (username: string, pinCode: string) => {
    try {
      setLoading(true);
      const response = await authApi.kidLogin(username, pinCode);
      const { token, kid } = response.data;

      localStorage.setItem('kidToken', token);
      localStorage.setItem('selectedKidId', kid.id!);
      setCurrentKid(kid);
      setParent(null);
      setKids([]);
      setUserType('kid');

      navigate(`/user/${kid.id}/targets`);
    } catch (err: any) {
      console.error('Kid login failed:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const refreshKids = useCallback(async () => {
    const parentToken = localStorage.getItem('parentToken');
    if (!parentToken) return;

    try {
      const response = await authApi.getMe(parentToken);
      if (response.data.type === 'parent') {
        setParent(response.data.data);
        const kidsData = response.data.kids || [];
        setKids(kidsData);

        // Update currentKid if it still exists
        const storedKidId = localStorage.getItem('selectedKidId');
        if (storedKidId) {
          const kid = kidsData.find((k: User) => k.id === storedKidId);
          setCurrentKid(kid || null);
        }
      }
    } catch (err) {
      console.error('Failed to refresh kids:', err);
    }
  }, []);

  const clearPendingInviteCode = useCallback(() => {
    sessionStorage.removeItem('pendingInviteCode');
    setPendingInviteCode(null);
  }, []);

  return (
    <AuthContext.Provider value={{
      parent,
      kids,
      currentKid,
      loading,
      isAuthenticated: !!parent || !!currentKid,
      userType,
      loginWithGoogle,
      loginKid,
      selectKid,
      logout,
      refreshKids,
      pendingInviteCode,
      clearPendingInviteCode
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};